import { EvaluatorSidebar } from "@client/components/round/allocator/EvaluatorSidebar/EvaluatorSidebar";
import {
  evaluatorTabOf,
  EvaluatorView,
} from "@client/components/round/allocator/EvaluatorView";
import { getMaxFdvLdt } from "@client/components/round/allocator/MVFGraph/MVFGraph";
import { runUnilateralAllocations } from "@client/components/round/allocator/runUnilateralAllocations";
import { withErrorBoundary } from "@client/components/utils/ErrorBoundary";
import { Popper } from "@client/components/utils/Popper";
import { Tooltip } from "@client/components/utils/Tooltip";
import InfoIcon from "@client/icons/InfoIcon";
import { LeftOff, LeftOn, RightOff, RightOn } from "@client/icons/Sidebar";
import userIsAdmin from "@client/utils/userIsAdmin";
import {
  hasTrackWithMatchingStatus,
  isUserFunder,
  isUserRoundAdminOrThisEvaluator,
} from "@engine/round/accessors";
import { Funder, Recommender } from "@engine/round/aggregate";
import { defaultOutsideDollarSubstitutionRate } from "@lib/constants";
import { Evaluator } from "@lib/types";
import { useContext, useEffect, useId, useRef, useState } from "react";
import { Redirect } from "wouter";
import { useEvaluatorEvaluationsCache } from "../../../components/round/allocator/useEvaluatorEvaluationsCache";
import { ClientSettingsContext, DispatchContext, StateContext } from "../Round";
import { MainRoot } from "./Aggregator";

const maxReasonableFdv = 1;

const toolbarButtonClassName =
  "flex items-center p-2 rounded-sm light:bg-gray-300 dark:bg-gray-800 hover:text-gray-600 focus:outline-none focus:ring-2 light:ring-blue-300 dark:ring-blue-600 cursor-pointer m-2";

export const EvaluatorSubpage = withErrorBoundary(
  ({ evaluator, view }: { evaluator: Funder | Recommender; view?: string }) => {
    const context = useContext(StateContext);
    const dispatch = useContext(DispatchContext);
    const { round, actingAsUserId: userId, mode } = context;
    const [useMatchingAdjustment, setUseMatchingAdjustment] = useState(false);
    const [showLeftSidebar, setShowLeftSidebar] = useState(true);
    const [showRightSidebar, setShowRightSidebar] = useState(true);

    useEffect(() => {
      function handleKeydown(e: KeyboardEvent) {
        const target = e.target as HTMLElement;
        if (
          target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable
        ) {
          return;
        }

        if (e.key === "[") {
          e.preventDefault();
          setShowLeftSidebar((prev) => !prev);
        }
        if (e.key === "]") {
          e.preventDefault();
          setShowRightSidebar((prev) => !prev);
        }
      }

      document.addEventListener("keydown", handleKeydown);
      return () => {
        document.removeEventListener("keydown", handleKeydown);
      };
    }, []);

    const evaluationsCache = useEvaluatorEvaluationsCache(
      evaluator,
      useMatchingAdjustment,
    );

    const evaluations = evaluationsCache.getEvaluatorEvaluations(evaluator.id);
    const { maxFDV, maxLDT } = getMaxFdvLdt(evaluations, round.holdProposalId);

    const defaultMaxLdt = Math.max(2_500_000, maxLDT);
    const defaultMaxFdv = Math.max(maxReasonableFdv, maxFDV);

    const [domains, setDomains] = useState<{
      xDomain: [number, number];
      yDomain: [number, number];
    }>({
      xDomain: [0, defaultMaxLdt],
      yDomain: [0, defaultMaxFdv],
    });
    const { xDomain, yDomain } = domains;

    const viewSupportsSidebar = ![
      "tranche-editor",
      "incremental-evals",
      "rating-table",
    ].includes(view ?? "");

    const { getSimulationHorizon } = useContext(ClientSettingsContext);

    const unilateralAllocations = runUnilateralAllocations(
      evaluations,
      getSimulationHorizon(round),
      round.holdProposalId,
    );

    const userCanUpdateEvaluations = isUserRoundAdminOrThisEvaluator(
      round,
      evaluator,
      userId,
    );

    const isZoomedToFit =
      xDomain[0] === 0 &&
      xDomain[1] === maxLDT &&
      yDomain[0] === 0 &&
      yDomain[1] === maxFDV;

    function zoomToFit() {
      setDomains({
        xDomain: [0, maxLDT],
        yDomain: [0, maxFDV],
      });
    }

    const toolbar = (
      <div className="flex flex-row w-full h-12 light:bg-gray-50 dark:bg-gray-900 border-b light:border-gray-200 dark:border-gray-800">
        <DropdownMenu
          evaluator={evaluator}
          useMatchingAdjustment={useMatchingAdjustment}
          setUseMatchingAdjustment={setUseMatchingAdjustment}
          userCanUpdateEvaluations={userCanUpdateEvaluations}
          maxLDT={maxLDT}
          maxFDV={maxFDV}
          setDomains={setDomains}
        />

        <div className="flex-1" />

        <button
          className={toolbarButtonClassName}
          onClick={() => {
            void dispatch({
              type: "NORMALIZE_EVALUATIONS",
              evaluatorId: evaluator.id,
            });
          }}
        >
          Normalize
        </button>
        <button
          className={toolbarButtonClassName}
          disabled={isZoomedToFit}
          onClick={zoomToFit}
        >
          Zoom to fit
        </button>
        <button
          onClick={() => setShowLeftSidebar(!showLeftSidebar)}
          className="cursor-pointer hover:bg-gray-300 rounded-md px-2 my-2"
          title="Toggle allocations sidebar ["
        >
          {showLeftSidebar ? <LeftOn /> : <LeftOff />}
        </button>
        <button
          onClick={() => setShowRightSidebar(!showRightSidebar)}
          className="cursor-pointer hover:bg-gray-300 rounded-md px-2 my-2"
          title="Toggle MVF table ]"
        >
          {showRightSidebar ? <RightOn /> : <RightOff />}
        </button>
      </div>
    );

    const isAdmin = userIsAdmin(userId, context);

    const accessiblePhases = [
      ...(isUserFunder(round, userId) || userCanUpdateEvaluations
        ? ["2.initial-evaluations"]
        : []),
      "3.full-discussion",
      "4.evals-freeze",
      "5.complete",
    ];

    if (
      !isAdmin &&
      !hasTrackWithMatchingStatus(round, userId, mode, accessiblePhases)
    ) {
      return <Redirect to="/" />;
    }

    return (
      <div className="flex flex-col w-full h-full">
        {toolbar}
        <div className="flex flex-row grow min-h-0">
          {viewSupportsSidebar && showLeftSidebar && (
            <EvaluatorSidebar
              evaluator={evaluator}
              evaluations={evaluations}
              unilateralAllocations={unilateralAllocations}
            />
          )}
          <MainRoot>
            <EvaluatorView
              unilateralAllocations={
                viewSupportsSidebar && showLeftSidebar
                  ? unilateralAllocations
                  : {}
              }
              evaluator={evaluator}
              evaluations={evaluations}
              userCanUpdateEvaluations={
                userCanUpdateEvaluations && !useMatchingAdjustment
              }
              mvfUpdate={evaluationsCache.mvfUpdate}
              mvfCommit={evaluationsCache.mvfCommit}
              roundId={round.id}
              node={evaluator.shortname}
              view={evaluatorTabOf(view)}
              showRightSidebar={showRightSidebar}
              domains={domains}
              setDomains={setDomains}
            />
          </MainRoot>
        </div>
      </div>
    );
  },
);

function DropdownMenu({
  evaluator,
  useMatchingAdjustment,
  setUseMatchingAdjustment,
  userCanUpdateEvaluations,
  maxLDT,
  maxFDV,
  setDomains,
}: {
  evaluator: Evaluator;
  useMatchingAdjustment: boolean;
  setUseMatchingAdjustment: (useMatchingAdjustment: boolean) => void;
  userCanUpdateEvaluations: boolean;
  maxLDT: number;
  maxFDV: number;
  setDomains: (domains: {
    xDomain: [number, number];
    yDomain: [number, number];
  }) => void;
}) {
  const dispatch = useContext(DispatchContext);
  const markersId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const ulRef = useRef<HTMLUListElement>(null);
  const [mouseDownInSlider, setMouseDownInSlider] = useState(false);

  const initialOutsideDollarSubstitutionRate =
    evaluator.type === "recommender"
      ? evaluator.outsideDollarSubstitutionRate
      : defaultOutsideDollarSubstitutionRate;
  const [outsideDollarSubstitutionRate, setOutsideDollarSubstitutionRate] =
    useState(initialOutsideDollarSubstitutionRate);

  useEffect(() => {
    setOutsideDollarSubstitutionRate(initialOutsideDollarSubstitutionRate);
  }, [initialOutsideDollarSubstitutionRate]);

  useEffect(() => {
    // Only update zoom when the mouse is not dragging the slider
    if (mouseDownInSlider) {
      return;
    }

    setDomains({
      xDomain: [0, maxLDT],
      yDomain: [0, maxFDV],
    });
  }, [maxLDT, maxFDV, mouseDownInSlider, setDomains]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        buttonRef.current &&
        ulRef.current &&
        !buttonRef.current.contains(event.target as Node) &&
        !ulRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <>
      <button
        ref={buttonRef}
        className={toolbarButtonClassName}
        onClick={() => setIsOpen(!isOpen)}
      >
        Matching Pledges
      </button>
      {useMatchingAdjustment && (
        <Tooltip
          title="MVFs are read-only when viewing with the matching adjustment"
          className="flex items-center p-2"
        >
          <span className="text-red-400">Read-only</span>
        </Tooltip>
      )}
      <Popper
        anchorEl={buttonRef.current}
        open={isOpen}
        placement="bottom-start"
      >
        <ul
          ref={ulRef}
          className="absolute left-0 z-10 mt-2 w-80 origin-top-left rounded-md light:bg-white dark:bg-gray-900 shadow-lg ring-1 ring-black/5 transition focus:outline-none data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-leave:duration-75 data-enter:ease-out data-leave:ease-in p-4 flex flex-col gap-2 dark:text-white"
        >
          <li>
            <div className="flex items-center gap-2">
              <label htmlFor="matching-adjustment">
                Show matching adjustment
              </label>
              <input
                id="matching-adjustment"
                type="checkbox"
                checked={useMatchingAdjustment}
                onChange={() =>
                  setUseMatchingAdjustment(!useMatchingAdjustment)
                }
              />
              <Tooltip title="Show the expected value of proposals, accounting for outside matching funds.">
                <span className="inline-block align-middle">
                  <InfoIcon />
                </span>
              </Tooltip>
            </div>
          </li>
          <li>
            <label htmlFor="outside-dollar-substitution-rate">
              <span className="flex gap-1">
                Outside Dollar Substitution Rate
                <Tooltip title="The rate at which the evaluator will substitute outside dollars for matching funds.">
                  <span className="inline-block align-middle">
                    <InfoIcon />
                  </span>
                </Tooltip>
              </span>
            </label>
            <input
              id="outside-dollar-substitution-rate"
              className="w-full border-0 p-0"
              type="range"
              disabled={!userCanUpdateEvaluations || !useMatchingAdjustment}
              min={0}
              max={100}
              value={outsideDollarSubstitutionRate * 100}
              onChange={(e) => {
                setOutsideDollarSubstitutionRate(+e.target.value / 100);
              }}
              list={markersId}
              onMouseDown={() => {
                setMouseDownInSlider(true);
              }}
              onMouseUp={() => {
                setMouseDownInSlider(false);

                if (
                  outsideDollarSubstitutionRate !==
                  initialOutsideDollarSubstitutionRate
                ) {
                  void dispatch({
                    type: "RECOMMENDER_OUTSIDE_DOLLAR_SUBSTITUTION_RATE_CHANGE",
                    evaluatorId: evaluator.id,
                    outsideDollarSubstitutionRate,
                  });
                }
              }}
            />
            <datalist id={markersId}>
              <option value="0" />
              <option value="25" />
              <option value="50" />
              <option value="75" />
              <option value="100" />
            </datalist>
            <div className="text-xs text-center">
              {useMatchingAdjustment &&
                outsideDollarSubstitutionRate.toFixed(2)}
            </div>
          </li>
        </ul>
      </Popper>
    </>
  );
}
